import { getApiAuthHeaders } from '../../infrastructure/requests/request-helpers';

const AUDIO_ENABLED_STORAGE_KEY = 'enable-word-audio';

export type WordAudioStatus =
	| { kind: 'allowed'; hasPremium: boolean; usedFreeWords: number; remainingFreeWords: number | null }
	| { kind: 'paywall'; hasPremium: false; usedFreeWords: number; remainingFreeWords: 0 }
	| { kind: 'unauthorized' };

export type WordAudioPlayResult = 'played' | 'paywall' | 'unauthorized' | 'error';

let activeAudio: HTMLAudioElement | null = null;
let activeAudioUrl: string | null = null;

function clearActiveAudioUrl(): void {
	if (activeAudioUrl) {
		URL.revokeObjectURL(activeAudioUrl);
		activeAudioUrl = null;
	}
}

function stopActiveAudio(): void {
	if (activeAudio) {
		activeAudio.pause();
		activeAudio.currentTime = 0;
		activeAudio = null;
	}
	clearActiveAudioUrl();
}

export function isWordAudioEnabled(): boolean {
	return localStorage.getItem(AUDIO_ENABLED_STORAGE_KEY)==='true';
}

export function setWordAudioEnabled(enabled: boolean): void {
	localStorage.setItem(AUDIO_ENABLED_STORAGE_KEY, enabled ? 'true':'false');
	if (!enabled) {
		stopActiveAudio();
	}
}

export async function getWordAudioStatus(): Promise<WordAudioStatus> {
	const response = await fetch('/api/tts/status', {
		method: 'GET',
		credentials: 'include',
		headers: getApiAuthHeaders()
	});

	if (response.status===401) {
		return { kind: 'unauthorized' };
	}

	const payload = await response.json().catch(() => ({} as Record<string, unknown>));
	const hasPremium = payload.hasPremium===true;
	const usedFreeWords = typeof payload.usedFreeWords==='number' ? payload.usedFreeWords:0;
	const remainingFreeWordsRaw = payload.remainingFreeWords;
	const remainingFreeWords =
		remainingFreeWordsRaw===null || typeof remainingFreeWordsRaw==='number'
			? remainingFreeWordsRaw
			:null;

	if (response.status===402) {
		return {
			kind: 'paywall',
			hasPremium: false,
			usedFreeWords,
			remainingFreeWords: 0
		};
	}

	if (!response.ok) {
		throw new Error(typeof payload.error==='string' ? payload.error:'Failed to check audio access');
	}

	return {
		kind: 'allowed',
		hasPremium,
		usedFreeWords,
		remainingFreeWords
	};
}

export async function playWordAudio(wordId: string): Promise<WordAudioPlayResult> {
	const response = await fetch(`/api/tts/play-word/${encodeURIComponent(wordId)}`, {
		method: 'GET',
		credentials: 'include',
		headers: getApiAuthHeaders()
	});

	if (response.status===401) return 'unauthorized';
	if (response.status===402) return 'paywall';
	if (!response.ok) return 'error';

	const blob = await response.blob();
	if (blob.size===0) return 'error';

	stopActiveAudio();

	const objectUrl = URL.createObjectURL(blob);
	const audio = new Audio(objectUrl);
	activeAudio = audio;
	activeAudioUrl = objectUrl;

	audio.onended = () => {
		activeAudio = null;
		clearActiveAudioUrl();
	};
	audio.onerror = () => {
		activeAudio = null;
		clearActiveAudioUrl();
	};

	try {
		await audio.play();
		return 'played';
	} catch {
		stopActiveAudio();
		return 'error';
	}
}

